/* eslint-disable complexity */
/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import {
  LoginAuthUserInputFields,
  LoginFormValidate
} from '@com-delta-idp/mnx-login-ui';
import { OauthAEMConfig } from '@com-delta-idp/mnx-oauth';
import {
  ACCOUNT_RECOVERY_PAGES,
  AnalyticsProfile,
  AnalyticsUser,
  AnalyticsUserAttributes,
  LINKS,
  USER_LOGGED_IN_STATUS
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { bytesToBase64 } from '@com-delta-idp/mnx-sitewide-shared-utils';
import { Irop } from '@com-delta-idp/mnx-utils';
import { sha256 } from '@noble/hashes/sha2.js';
import { BehaviorSubject } from 'rxjs';
import { DATALAYER_PAGE_EVENTS } from '../constants/analytics-constants';
import { IROP_ERROR } from '../constants/login-constant';
import {
  DashboardDataResponse,
  loginAndGetDashboardResponse
} from '../models/login-authentication-screen.interface';
import { FacadeSupportService } from './facade-support.service';

@Injectable({
  providedIn: 'root'
})
export class FacadeHelperService extends FacadeSupportService {
  loginPageType$ = new BehaviorSubject('');
  loginDashboardResponse: loginAndGetDashboardResponse =
    {} as loginAndGetDashboardResponse;

  getDashBrdDataBffCall(
    userInputFields: LoginAuthUserInputFields | undefined,
    tokenResponse: string,
    authenticationConfig: OauthAEMConfig,
    fromApp = '',
    standalonePage = false
  ) {
    const excludeList = authenticationConfig.BFF_CODES_TO_IGNORE;
    this.pingAdapter.redirectUrl = this.redirectUrl;
    this.loginResponseService
      .getLoginResponse(
        authenticationConfig,
        this.adapter.getNewBrdDashDataRequest(
          userInputFields,
          tokenResponse,
          fromApp,
          standalonePage,
          authenticationConfig?.TOKEN_HEADER
            ? authenticationConfig.TOKEN_HEADER
            : false
        ),
        authenticationConfig?.TOKEN_HEADER ? tokenResponse : ''
      )
      .subscribe((response) => {
        if (response?.errors) {
          const [{ code = '', description = '' }] = response.errors;
          this.loggingService.log(`${code}: ${description}`);

          if (
            this.loginAuthenticationHelperService.checkForExcludeList(
              excludeList,
              response
            )
          ) {
            this.pingAdapter.makeStartSloCall(authenticationConfig);
          } else {
            const errorResponse: { errorId: string; accountLocked?: boolean } =
              { errorId: `ERROR_MSG_${response?.errors[0]?.code}` };
            this.rememberMeWrapperService.swapAuthForAccessTokens = {};
            this.rememberMeWrapperService.errorFromTokenCall = true;
            this.rememberMeWrapperService.forcePromptLoginFlow = true;
            this.loginResponseService.loginResponse = undefined;
            this.updateLoaderAndDisableButton(false);
            this.displayPingError(errorResponse);
            this.errorAdobeTracker$.next(true);
          }
          this.dataLayerServcie.updateAdobeDataLayerError(
            response?.smNumber,
            response?.membershipLevel,
            response?.smeCardTypes,
            Number(response?.smBalance),
            response?.primaryEmailAddress
          );
        } else {
          this.loginDashboardResponse = response;
          if (
            this.loginAuthenticationHelperService.checkForNotLoggedin(response)
          ) {
            this.pingAdapter.makeStartSloCall(authenticationConfig);
          } else {
            this.disableSpinnerForAccountRecovery(response);
            this.processLoginDashboard(
              response,
              authenticationConfig,
              standalonePage,
              tokenResponse
            );
          }
        }
      });
  }

  disableSpinnerForAccountRecovery(response: loginAndGetDashboardResponse) {
    if (!response.hasEmail || !response.isEmailValid || !response.hasSQA)
      this.updateLoaderAndDisableButton(false);
  }

  processLoginDashboard(
    response: loginAndGetDashboardResponse,
    authenticationConfig: OauthAEMConfig,
    standalonePage = false,
    tokenResponse?: string
  ) {
    const data = this.buildAnalyticsProfile(response);
    const loginPage = this.loginPageType$.getValue();

    this.analyticsService.setLoginGlobalStatus(data, loginPage, true);
    this.dataLayerServcie.triggerLoginDataLayerEvents(
      DATALAYER_PAGE_EVENTS.LOGIN_SUCCESS
    );
    this.dataLayerServcie.updateMedallionStatusAndSkyMilesNumber(
      response?.smNumber,
      response?.membershipLevel
    );
    this.pingAdapter.fromApplication = this.fromApplication;
    this.pingAdapter.redirectUrl = this.redirectUrl;
    this.pingAdapter.processLoginDashboardSuccessResponse(
      response,
      authenticationConfig,
      standalonePage,
      tokenResponse
    );
  }

  async loginPostWithBFF(userInputFields?: LoginAuthUserInputFields) {
    this.errorAdobeTracker$.next(false);
    if (
      this.loginAuthenticationHelperService.validityCheckForFormSubmit(
        userInputFields,
        this.authenticationConfig
      )
    ) {
      const tokenResponse =
        await this.pingAdapter.authenicateUserAndGetAccessToken(
          userInputFields,
          this.authenticationConfig
        );
      this.loginRememberMeUtils.sendTokenServiceWorker(
        this.authenticationConfig,
        tokenResponse
      );
      this.loginAuthenticationHelperService.setLocalStorageForPersistentLogin(
        tokenResponse?.encyToken,
        tokenResponse?.uid
      );
      this.loginAuthenticationHelperService.setPromptCookie(
        userInputFields,
        this.authenticationConfig
      );
      if (
        this.loginAuthenticationHelperService.checkForAccessToken(tokenResponse)
      ) {
        this.getDashBrdDataBffCall(
          userInputFields,
          tokenResponse?.access_token,
          this.authenticationConfig,
          this.fromApplication,
          this.standalonePage
        );
      } else {
        this.handleLoginError(tokenResponse, userInputFields);
      }
    } else {
      this.loginDasboardService
        .loginQueryPage()
        .subscribe(async (response: { queryParams: string }) => {
          if (response.queryParams) {
            await this.custLoginBffCall(
              userInputFields,
              response.queryParams,
              this.standalonePage
            );
          }
        });
    }
  }

  async onFormSubmit(
    submit = false,
    userInputFields?: LoginAuthUserInputFields
  ) {
    this.iropService.getContent().subscribe(
      (irop: Irop) => {
        if (irop.iropType) {
          this.updateLoaderAndDisableButton(false);
          const errorMessage =
            this.loginAuthenticationHelperService.getErrorMessageFromAem(
              this.aemContent,
              IROP_ERROR
            );
          this.loginAuthenticationScreenContent = {
            ...this.loginAuthenticationScreenContent,
            errorCode: IROP_ERROR,
            errorMessage
          };
          this.resetForm();
          this.loginContent$.next(this.loginAuthenticationScreenContent);
        } else {
          this.submitPostIropCheck(submit, userInputFields);
        }
      },
      () => {
        this.submitPostIropCheck(submit, userInputFields);
      }
    );
  }

  submitPostIropCheck(
    submit = false,
    userInputFields?: LoginAuthUserInputFields
  ) {
    if (submit) {
      LoginFormValidate.touchControlsOnSubmit(this.getContent());
      this.getContent().loginFormAttributes.submitAttempt = true;
      if (this.getContent().formGroup.valid) {
        this.dataLayerServcie.updateUserNameAsLoginType(userInputFields);
        if (userInputFields) {
          this.storeUserInputForAnalytics(userInputFields);
        }
        this.updateLoaderAndDisableButton(true);
        this.loginPostWithBFF(userInputFields);
      } else {
        this.updateErrorCountAndDisplayAlertBanner();
      }
    }
  }

  private handleLoginError(
    tokenResponse: {
      errorId?: string;
      error?: string;
    },
    userInputFields?: LoginAuthUserInputFields
  ): void {
    this.updateLoaderAndDisableButton(false);
    const errorId = tokenResponse?.errorId || tokenResponse?.error || '';

    if (errorId === 'ERROR_MSG_SEC_CP_CHALLENGE') {
      if (this.authenticationConfig.ERROR_428) {
        this.loggingService.log(
          'Status Code: 428 - Display Code Challenge: ' + errorId
        );
        this.currentUserInputFields = userInputFields;
      }
      this.flowIdForTheTransaction = undefined;
    } else {
      this.displayPingError({ errorId });
      this.errorAdobeTracker$.next(true);

      if (errorId === 'ERROR_MSG_1A8B') {
        this.flowIdForTheTransaction = undefined;
      }
    }
  }

  private storeUserInputForAnalytics(
    userInputFields: LoginAuthUserInputFields
  ): void {
    this.currentUserInputFields = userInputFields;
  }

  sendTokenServiceWorker(tokenResponse: {
    access_token: string;
    expires_in: string;
  }) {
    if (
      this.authenticationConfig?.ENABLE_NEW_PERSISTENT_LOGIN &&
      navigator.serviceWorker &&
      navigator.serviceWorker.controller
    ) {
      this.coreUtilService.sendMessage({
        type: 'ADD_TOKEN',
        token: tokenResponse?.access_token,
        issueTime: new Date().getTime(),
        expiresIn: parseInt(tokenResponse?.expires_in)
      });
      this.coreUtilService.sendMessage({ type: 'DELETE_STATUS' });
    }
  }

  getStoredRmeCookieHelper(dataObj: Partial<DashboardDataResponse>) {
    if (dataObj.rememberMeLastName) {
      this.getContent().formGroup.controls['userId'].setValue({
        value: dataObj.rememberMeID
      });
      this.getContent().formGroup.controls['keepMeLoggedIn'].setValue({
        value: dataObj.rememberMeIndicator
      });
      this.getContent().formGroup.controls['lastName'].setValue({
        value: dataObj.rememberMeLastName
      });
    } else {
      this.getContent().formGroup.controls['userId'].setValue({
        value: dataObj.rememberMeID
      });
      this.getContent().formGroup.controls['keepMeLoggedIn'].setValue({
        value: dataObj.rememberMeIndicator
      });
    }
  }

  getStoredRmeCookie() {
    if (
      this.coreUtilService.getLocalStorageWithExpiry('rme-ui') &&
      !this.authenticationConfig.PING_FLOW_ENABLED
    ) {
      this.loginDasboardService
        .getVsDashboardResponse()
        .subscribe((data: Partial<DashboardDataResponse>) => {
          const dataObj: Partial<DashboardDataResponse> =
            data as Partial<DashboardDataResponse>;
          if (dataObj && dataObj.rememberMeID && dataObj.rememberMeIndicator) {
            this.getStoredRmeCookieHelper(dataObj);
          }
        });
    }
  }

  private buildAnalyticsProfileAttributes(
    response: loginAndGetDashboardResponse
  ): AnalyticsUserAttributes {
    return {
      is_logged_in: response?.loggedIn
        ? USER_LOGGED_IN_STATUS.AUTHENTICATED
        : USER_LOGGED_IN_STATUS.UNAUTHENTICATED,
      login_method: this.currentUserInputFields?.lastName
        ? 'UserName'
        : 'Skymiles',
      login_state: this.getLoginState(response?.loggedIn ?? false)
    };
  }

  private buildAnalyticsProfileData(
    response: loginAndGetDashboardResponse
  ): AnalyticsProfile {
    const getValueOrEmpty = (value: string | undefined | null): string => {
      return value === undefined || value === null ? '' : value;
    };

    const emailAddress = getValueOrEmpty(response?.primaryEmailAddress);
    const encryptedEmail = emailAddress
      ? bytesToBase64(sha256(new TextEncoder().encode(emailAddress)))
      : '';

    return {
      skymiles_number: getValueOrEmpty(response?.smNumber),
      medalion_tier: getValueOrEmpty(response?.membershipLevel),
      cobrand_type: getValueOrEmpty(response?.smeCardTypes),
      available_miles: Number(getValueOrEmpty(response?.smBalance)),
      email: emailAddress,
      email_domain:
        emailAddress && emailAddress.split('@').length > 1
          ? emailAddress.split('@')[1]
          : '',
      company_name: getValueOrEmpty(response?.smeCompanyName),
      encrypted_email: encryptedEmail
    };
  }

  private buildAnalyticsProfile(
    response: loginAndGetDashboardResponse
  ): AnalyticsUser {
    return {
      attributes: this.buildAnalyticsProfileAttributes(response),
      profile: this.buildAnalyticsProfileData(response)
    } as AnalyticsUser;
  }

  private getLoginState(isWarmState: boolean): string {
    return isWarmState ? USER_LOGGED_IN_STATUS.HOT : USER_LOGGED_IN_STATUS.COLD;
  }

  checkLoginPage(): string {
    return this.standalonePage
      ? ACCOUNT_RECOVERY_PAGES.STANDALONE
      : this.router.url.endsWith(LINKS.SEATS_URL)
      ? ACCOUNT_RECOVERY_PAGES.CENTER_PAGE
      : ACCOUNT_RECOVERY_PAGES.GLOBAL_HEADER;
  }
}
